import {
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";

import { en, type Dictionary, type TranslationKey } from "./translations/en";
import { DEFAULT_LANGUAGE, isSupportedLanguage, languageMeta, type LanguageCode } from "./languages";

/**
 * Interface translation.
 *
 * WHY HAND-ROLLED RATHER THAN react-i18next
 * The app has seven runtime dependencies and a Jest ESM setup that has already
 * proved brittle — three suites still fail to load purely because of module
 * mocking. i18next would add a provider, a plugin chain and another module every
 * test has to mock, in exchange for features this app does not need:
 * pluralisation rules beyond simple counts, namespaces, backend loaders,
 * context/gender variants. What is actually needed is dictionary lookup,
 * `{brace}` interpolation, English fallback and RTL — about a hundred lines.
 *
 * WHY THE ENGLISH FALLBACK IS PER-KEY
 * A partly-translated language shows real English for anything missing rather
 * than a raw key like `doubt.submit`. That keeps the UI usable while a
 * translation is still being filled in, and `coverage` below lets the profile
 * screen say honestly how complete the language is instead of pretending.
 */

/** Loaded lazily so a student on Tamil never downloads the other twelve. */
const LOADERS: Record<Exclude<LanguageCode, "en">, () => Promise<Dictionary>> = {
    hi: () => import("./translations/hi").then((m) => m.hi),
    bn: () => import("./translations/bn").then((m) => m.bn),
    mr: () => import("./translations/mr").then((m) => m.mr),
    te: () => import("./translations/te").then((m) => m.te),
    ta: () => import("./translations/ta").then((m) => m.ta),
    gu: () => import("./translations/gu").then((m) => m.gu),
    kn: () => import("./translations/kn").then((m) => m.kn),
    ml: () => import("./translations/ml").then((m) => m.ml),
    pa: () => import("./translations/pa").then((m) => m.pa),
    or: () => import("./translations/or").then((m) => m.or),
    as: () => import("./translations/as").then((m) => m.as),
    ur: () => import("./translations/ur").then((m) => m.ur)
};

// Renamed from the previous brand ("examintel:language:v1") with no migration
// on purpose: this is only a paint-flash cache. The server copy on /users/me is
// the source of truth and overwrites it on load, so the worst case for a browser
// carrying the old key is one frame of English before the real choice arrives.
const STORAGE_KEY = "greenagentick:language:v1";
const TOTAL_KEYS = Object.keys(en).length;

/**
 * Read the cached choice synchronously, before React renders.
 *
 * The server is the source of truth, but waiting for `/users/me` would render
 * one frame of English and then repaint — a visible flash on every page load in
 * the very language the student chose to avoid. Cache locally, correct from the
 * server if they differ.
 */
const readCachedLanguage = (): LanguageCode => {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        return isSupportedLanguage(stored) ? stored : DEFAULT_LANGUAGE;
    } catch {
        // Private browsing / storage disabled — English is a fine default.
        return DEFAULT_LANGUAGE;
    }
};

const writeCachedLanguage = (code: LanguageCode) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, code);
    } catch {
        /* non-fatal: the server copy still persists the choice */
    }
};

/** Replace {placeholders}. Unknown names are left visible rather than blanked —
 *  a literal "{name}" on screen is an obvious bug; a silent empty space is not. */
const interpolate = (template: string, values?: Record<string, string | number>): string => {
    if (!values) return template;
    return template.replace(/\{(\w+)\}/g, (match, key: string) =>
        key in values ? String(values[key]) : match
    );
};

interface I18nContextValue {
    language: LanguageCode;
    /** Translate a key, with optional {placeholder} values. */
    t: (key: TranslationKey, values?: Record<string, string | number>) => string;
    /** Change language locally. Persisting to the server is the caller's job
     *  (see `useLanguagePreference`) so this stays usable pre-login. */
    setLanguage: (code: LanguageCode) => void;
    /** 0..1 share of keys this language actually translates. */
    coverage: number;
    isRtl: boolean;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export const I18nProvider = ({ children }: { children: ReactNode }) => {
    const [language, setLanguageState] = useState<LanguageCode>(readCachedLanguage);
    const [dictionary, setDictionary] = useState<Dictionary>({});

    useEffect(() => {
        if (language === "en") {
            setDictionary({});
            return;
        }
        let cancelled = false;
        LOADERS[language]()
            .then((loaded) => {
                // Guard against a stale load winning after a fast re-switch.
                if (!cancelled) setDictionary(loaded);
            })
            .catch(() => {
                // A failed chunk load must not blank the app; English still renders.
                if (!cancelled) setDictionary({});
            });
        return () => {
            cancelled = true;
        };
    }, [language]);

    /** Direction and `lang` belong on <html>, not a wrapper div: they are what
     *  the browser uses for text selection, spellcheck and screen readers. */
    useEffect(() => {
        const meta = languageMeta(language);
        document.documentElement.lang = language;
        document.documentElement.dir = meta.rtl ? "rtl" : "ltr";
    }, [language]);

    const setLanguage = useCallback((code: LanguageCode) => {
        setLanguageState(code);
        writeCachedLanguage(code);
    }, []);

    const value = useMemo<I18nContextValue>(() => {
        const translated = Object.keys(dictionary).length;
        return {
            language,
            setLanguage,
            coverage: language === "en" ? 1 : translated / TOTAL_KEYS,
            isRtl: Boolean(languageMeta(language).rtl),
            t: (key, values) => interpolate(dictionary[key] ?? en[key] ?? key, values)
        };
    }, [dictionary, language, setLanguage]);

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

/**
 * Falls back to English rather than throwing when no provider is present.
 *
 * Deliberate: dozens of components call this, and many are rendered in isolation
 * by unit tests. Throwing would mean every one of those tests has to wrap in a
 * provider to test something unrelated to language.
 */
export const useTranslation = (): I18nContextValue => {
    const context = useContext(I18nContext);
    if (context) return context;
    return {
        language: DEFAULT_LANGUAGE,
        setLanguage: () => undefined,
        coverage: 1,
        isRtl: false,
        t: (key, values) => interpolate(en[key] ?? key, values)
    };
};
